import React, { useEffect, useState } from 'react';
import './App.css';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation
} from "react-router-dom";
import { AllCoursesPage, 
         CoursePage,
         Home,
         Premium,
         CheckoutSinglePage,
         LessonPage,
         SignupPage,
         Espera,
         Direto,
         AnswerList, 
         ForumPage, 
         TopicPage, 
         ChatsPage,
         AccountPage,
         AccountEditPage,
         AccountPublicPage,
         PostPage,
         FeedPage,
         ChatPage, 
         DashboardPage, 
         LeaderboardPage,
         JobPage,
         SobrePage,
         AboutPage,
         ContactPage,
         ConteudoPage,
         CircuitosPage,
         AvaliacoesPage,
         RequisitosPage,
         TeoriaPage,
         PraticaPage,
         EditCreditCardPage,
         DuvidasPage,
         AntigosPage, 
         CoursePagePublic,
         FreePage,
         NetworkPage,
         NewsPage} from "./pages/index";
import PoliticaDePrivacidade from "./pages/legal/politicadeprivacidade.js";
import TermosDeUso from "./pages/legal/termosdeuso";
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import AdminLayout from './pages/admin/AdminLayout';
import AdminStatsPage from './pages/admin/AdminStatsPage';
import AdminCheckoutPage from './pages/admin/AdminCheckoutPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminContentPage from './pages/admin/AdminContentPage';
import AdminCoursePage from './pages/admin/AdminCoursePage';
import AdminLessonPage from './pages/admin/AdminLessonPage';
import Login from "./pages/login";
import {Signup} from './pages/account/signup';
import Logon from './pages/account/logon';
import VerifyAccount from './pages/account/verify';
import Recover from "./pages/account/recover.js";
import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';
import CookieConsent from './pages/cookie_consent';
import CtaUpgradeBanner from './pages/cta_upgrade_banner.js';
import { useAuth } from './services/AuthContext.js'; // Import your AuthContext
import { Buffer } from 'buffer';
import NotFound from './pages/not_found/not_found.js';
import axios from "axios";
import { Dashboard } from './pages/course_list.js';
import Leaderboard from './pages/leaderboard.js';
import Avaliacoes from './pages/avaliacoes.js';
import Requisitos from './pages/requisitos.js';
import EditCreditCard from './pages/checkout/edit_credit_card.js';
import { captureGclid } from './utils/gclid';
import { captureFbclid } from './utils/metaAds';

window.Buffer = Buffer;
axios.defaults.baseURL = process.env.REACT_APP_BACKEND_API_BASE_ENDPOINT;

const PageLogger = () => {
  const [userId, setUserId] = useState(null);
  const [cookieId, setCookieId] = useState(null);
  const { user, loading, getIdToken } = useAuth();

  useEffect(() => {
    if (loading) {
      return;
    }
    const logPageView = async () => {
      const token = await getIdToken();
      await axios.post("/user_log/log-view-page", 
      {
        cookie_id: cookieId,
        full_url: window.location.href,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
      );
    };

    if (cookieId) {
      logPageView();
    }
  }, [window.location.href, cookieId, userId]);

  useEffect(() => {
    if (user?.cognitoUser?.username) {
      setUserId(user.cognitoUser.username);
    }
  }, [user]);

  useEffect(() => {
    let existingId = Cookies.get("uid");

    if (!existingId) {
      existingId = uuidv4();
      Cookies.set("uid", existingId, { expires: 365 });
    }
    setCookieId(existingId);
  }, []);

  return null; // This component doesn't render anything
}

const FbclidCapture = () => {
  const location = useLocation();

  useEffect(() => {
    captureFbclid();
  }, [location.search]);

  return null;
}

export default function App() {
  useEffect(() => {
    // Check if the user already has a UID stored in a cookie
    let userId = Cookies.get('uid');

    // If not, generate a new UID and set it as a cookie
    if (!userId) {
      userId = uuidv4();
      Cookies.set('uid', userId, { expires: 365 }); // Cookie expires in 1 year
    }

    // Persist first-touch gclid so attribution survives browsing before checkout
    captureGclid();
    // Persist last-touch fbclid (overwrite when a new one appears in the URL)
    captureFbclid();
  }, []);

  return (
    <Router>
      <PageLogger />
      <FbclidCapture />
      {/* <CookieConsent /> */}
      {/* <CtaUpgradeBanner /> */}
      <Routes>
        {/* Homepage */}
        <Route path="/" element={<Premium />} />
        {/* Free */}
        <Route path="/free" element={<FreePage />} />
        {/* Premium */}
        <Route path="/premium" element={<Premium />} />
        {/* Single-step checkout */}
        <Route path="/checkout" element={<CheckoutSinglePage />} />
        {/* Page 'Dúvidas' */}
        <Route path="/duvidas" element={<DuvidasPage />} />
        {/* Page 'Antigos' */}
        <Route path="/antigos" element={<AntigosPage />} />
        {/* Page 'About' (Nossa história) */}
        <Route path="/about" element={<AboutPage />} />
        {/* Page 'Contact' (Fale conosco) */}
        <Route path="/contact" element={<ContactPage />} />
        {/* Course Landing Page */}
        <Route path="/public-course-page/:courseSlug" element={<CoursePagePublic/>}/>
        {/* Course Content Page - Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/news"
          element={
            <ProtectedRoute>
              <NewsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/leaderboard"
          element={
            <ProtectedRoute>
              <LeaderboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/network"
          element={
            <ProtectedRoute>
              <NetworkPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/allcourses"
          element={
            <ProtectedRoute>
              <AllCoursesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coursepage/:courseId"
          element={
            <ProtectedRoute>
              <CoursePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/courses/:courseId/sections/:sectionId/lessons/:lessonId"
          element={
            <ProtectedRoute>
              <LessonPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-credit-card"
          element={
            <ProtectedRoute>
              <EditCreditCardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/topic/:topicId"
          element={
            <ProtectedRoute>
              <TopicPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chats"
          element={
            <ProtectedRoute>
              <ChatsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat/:chatCode"
          element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/job/:jobId"
          element={
            <ProtectedRoute>
              <JobPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <AccountPage/>
            </ProtectedRoute>
          }
        />
        <Route
          path="/account-edit"
          element={
            <ProtectedRoute>
              <AccountEditPage/>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/:slug"
          element={
            <ProtectedRoute>
              <AccountPublicPage/>
            </ProtectedRoute>
          }
        />
        <Route
          path="/post/:postId"
          element={
            <ProtectedRoute>
              <PostPage/>
            </ProtectedRoute>
          }
        />
        <Route
          path="/feed"
          element={
            <ProtectedRoute>
              <FeedPage/>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<Navigate to="stats" replace />} />
          <Route path="stats" element={<AdminStatsPage />} />
          <Route path="checkout" element={<AdminCheckoutPage />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="content" element={<AdminContentPage />} />
          <Route path="content/:courseCode" element={<AdminCoursePage />} />
          <Route
            path="content/:courseCode/lessons/:sectionCode/:lessonCode"
            element={<AdminLessonPage />}
          />
        </Route>
        {/* Authentication Pages */}
        <Route path='/logon' element={<Logon />}/>
        <Route path="/verify" element={<VerifyAccount/>}/>
        <Route path="/recover" element={<Recover/>}/>
        <Route path='/signup' element={<SignupPage />}/>
        {/* Legal Stuff */}
        <Route path="/politicadeprivacidade" element={<PoliticaDePrivacidade />} />
        <Route path="/termosdeuso" element={<TermosDeUso />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}
